import { ScholarshipAwardDurationDto } from '@cradua/data';
import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as ScholarshipAwardDurationActions from './scholarshipawardduration.actions';

export const SCHOLARSHIP_AWARD_DURATION_FEATURE_KEY =
  'scholarshipawardduration';

export interface ScholarshipAwardDurationState
  extends EntityState<ScholarshipAwardDurationDto> {
  selectedScholarshipAwardDurationId?: string | number; // which ScholarshipAwardDuration record has been selected
  loaded: boolean; // has the ScholarshipAwardDuration list been loaded
  error?: string | null; // last none error (if any)
}

export interface ScholarshipAwardDurationPartialState {
  readonly [SCHOLARSHIP_AWARD_DURATION_FEATURE_KEY]: ScholarshipAwardDurationState;
}

export const scholarshipAwardDurationAdapter: EntityAdapter<ScholarshipAwardDurationDto> = createEntityAdapter<
  ScholarshipAwardDurationDto
>({
  selectId: (scholarshipAwardDuration) => scholarshipAwardDuration.id,
  sortComparer: false,
});

export const initialState: ScholarshipAwardDurationState = scholarshipAwardDurationAdapter.getInitialState(
  {
    // set initial required properties
    loaded: false,
    error: null,
    selectedScholarshipAwardDurationId: null,
  }
);

const scholarshipAwardDurationReducer = createReducer(
  initialState,
  on(ScholarshipAwardDurationActions.loadScholarshipAwardDuration, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ScholarshipAwardDurationActions.loadScholarshipAwardDurationSuccess,
    (state, { scholarshipAwardDuration }) =>
      scholarshipAwardDurationAdapter.addAll(scholarshipAwardDuration, {
        ...state,
        loaded: false,
      })
  ),
  on(
    ScholarshipAwardDurationActions.loadScholarshipAwardDurationFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(ScholarshipAwardDurationActions.addScholarshipAwardDuration, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ScholarshipAwardDurationActions.addScholarshipAwardDurationSuccess,
    (state, { newScholarshipAwardDuration }) =>
      scholarshipAwardDurationAdapter.addMany(newScholarshipAwardDuration, {
        ...state,
        loaded: false,
      })
  ),
  on(
    ScholarshipAwardDurationActions.addScholarshipAwardDurationFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(
    ScholarshipAwardDurationActions.updateScholarshipAwardDuration,
    (state) => ({
      ...state,
      loaded: true,
      error: null,
    })
  ),
  on(
    ScholarshipAwardDurationActions.updateScholarshipAwardDurationSuccess,
    (state, { updateScholarshipAwardDuration }) =>
      scholarshipAwardDurationAdapter.upsertMany(
        updateScholarshipAwardDuration,
        { ...state, loaded: false }
      )
  ),
  on(
    ScholarshipAwardDurationActions.updateScholarshipAwardDurationFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(
    ScholarshipAwardDurationActions.removeScholarshipAwardDuration,
    (state) => ({
      ...state,
      loaded: true,
      error: null,
    })
  ),
  on(
    ScholarshipAwardDurationActions.removeScholarshipAwardDurationSuccess,
    (state, { ids }) =>
      scholarshipAwardDurationAdapter.removeMany(ids, {
        ...state,
        loaded: false,
      })
  ),
  on(
    ScholarshipAwardDurationActions.removeScholarshipAwardDurationFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  )
);

export function reducer(
  state: ScholarshipAwardDurationState | undefined,
  action: Action
) {
  return scholarshipAwardDurationReducer(state, action);
}
